import React, { useEffect, useState } from 'react';
import { Loader2, TrendingUp, TrendingDown, ShoppingBag, Euro, Percent, Package, FileSpreadsheet, ChevronLeft, ChevronRight } from 'lucide-react';
import { ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid, PieChart, Pie, Cell, BarChart } from 'recharts';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { reportsAPI, exportAPI } from '../services/api';
import SalesTargets from './SalesTargets';

const COLORS = ['#f97316', '#1e3a5f', '#16a34a', '#0ea5e9', '#a855f7', '#eab308', '#ef4444', '#64748b'];
const money = (v) => `${Number(v || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
const shiftMonth = (ym, delta) => { const [y, m] = ym.split('-').map(Number); const d = new Date(y, m - 1 + delta, 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
const monthLabel = (ym) => { const [y, m] = ym.split('-').map(Number); return new Date(y, m - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' }); };

const Kpi = ({ icon: Icon, label, value, prev, format = money, testId, invert = false }) => {
  const delta = prev ? ((value - prev) / prev) * 100 : null;
  const up = delta != null && delta >= 0;
  return (
    <div className="bg-white rounded-lg shadow-sm p-4" data-testid={testId}>
      <div className="flex items-center justify-between text-gray-500 text-xs"><span>{label}</span><Icon size={16} className="text-orange-500" /></div>
      <p className="text-2xl font-bold text-gray-900 mt-1">{format(value)}</p>
      <p className={`text-xs mt-1 flex items-center gap-1 ${delta == null ? 'text-gray-400' : (up !== invert) ? 'text-green-700' : 'text-red-600'}`}>
        {delta == null ? 'Pas de comparaison' : <>{up ? <TrendingUp size={12} /> : <TrendingDown size={12} />} {up ? '+' : ''}{delta.toFixed(1)} % vs mois précédent ({format(prev)})</>}
      </p>
    </div>
  );
};

// Admin: monthly sales report with charts
const AdminReportsPanel = ({ onOpenSettings }) => {
  const now = new Date();
  const [month, setMonth] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`);
  const [data, setData] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    setData(null);
    reportsAPI.monthly(month).then(setData).catch(() => { toast.error('Rapport indisponible'); setData({ error: true }); });
  }, [month]);

  const exportMonth = async () => {
    setExporting(true);
    try { await exportAPI.xlsx(data.date_from, data.date_to); toast.success('Export téléchargé'); } catch { toast.error('Export impossible'); } finally { setExporting(false); }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Rapport de ventes</h1>
          <p className="text-sm text-gray-500">Commandes annulées et remboursées exclues · marge calculée avec le prix d'achat actuel du fournisseur de référence.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button type="button" data-testid="report-prev-month" onClick={() => setMonth(m => shiftMonth(m, -1))} className="p-2 rounded-md border hover:bg-gray-50"><ChevronLeft size={16} /></button>
          <input type="month" data-testid="report-month-input" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="border rounded-md px-3 py-1.5 text-sm" />
          <button type="button" data-testid="report-next-month" onClick={() => setMonth(m => shiftMonth(m, 1))} className="p-2 rounded-md border hover:bg-gray-50"><ChevronRight size={16} /></button>
          <Button data-testid="report-export" onClick={exportMonth} disabled={!data || data.error || exporting} className="bg-green-600 hover:bg-green-700">{exporting ? <Loader2 size={16} className="mr-2 animate-spin" /> : <FileSpreadsheet size={16} className="mr-2" />} Exporter ce mois en Excel</Button>
        </div>
      </div>

      {!data ? <div className="flex justify-center py-16"><Loader2 className="animate-spin text-orange-500" /></div> : data.error ? null : (
        <div className="space-y-6" data-testid="report-content">
          <h2 className="text-lg font-semibold text-gray-800 capitalize" data-testid="report-month-label">{monthLabel(data.month)}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <Kpi icon={Euro} label="CA TTC" value={data.kpis.revenue_ttc} prev={data.previous.revenue_ttc} testId="kpi-revenue" />
            <Kpi icon={Euro} label="CA HT" value={data.kpis.revenue_ht} prev={data.previous.revenue_ht} testId="kpi-revenue-ht" />
            <Kpi icon={Percent} label={`Marge HT (${data.kpis.margin_rate} %)`} value={data.kpis.margin_ht} prev={data.previous.margin_ht} testId="kpi-margin" />
            <Kpi icon={ShoppingBag} label="Commandes" value={data.kpis.orders} prev={data.previous.orders} format={(v) => String(v)} testId="kpi-orders" />
            <Kpi icon={ShoppingBag} label="Panier moyen" value={data.kpis.avg_basket} prev={data.previous.avg_basket} testId="kpi-basket" />
            <Kpi icon={Package} label="Unités vendues" value={data.kpis.units} prev={data.previous.units} format={(v) => String(v)} testId="kpi-units" />
          </div>

          <SalesTargets targets={data.targets} onOpenSettings={onOpenSettings} />

          <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4 xl:col-span-2" data-testid="chart-daily">
              <h3 className="font-semibold text-gray-900 mb-3">CA & marge par jour</h3>
              <ResponsiveContainer width="100%" height={260}>
                <ComposedChart data={data.daily}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, n) => (n === 'Commandes' ? v : money(v))} labelFormatter={(d) => `Jour ${d}`} />
                  <Legend />
                  <Bar dataKey="revenue" name="CA TTC" fill="#f97316" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="margin" name="Marge HT" stroke="#1e3a5f" strokeWidth={2} dot={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4" data-testid="chart-categories">
              <h3 className="font-semibold text-gray-900 mb-3">CA par catégorie</h3>
              {data.categories.length === 0 ? <p className="text-sm text-gray-400 py-10 text-center">Aucune vente ce mois.</p> : (
                <ResponsiveContainer width="100%" height={260}>
                  <PieChart>
                    <Pie data={data.categories} dataKey="revenue" nameKey="category" innerRadius={55} outerRadius={95} paddingAngle={2}>
                      {data.categories.map((c, i) => <Cell key={c.slug} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip formatter={(v) => money(v)} />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                  </PieChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow-sm p-4" data-testid="chart-top">
              <h3 className="font-semibold text-gray-900 mb-3">Top 10 des pièces (unités vendues)</h3>
              {data.top_products.length === 0 ? <p className="text-sm text-gray-400 py-10 text-center">Aucune vente ce mois.</p> : (
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={data.top_products} layout="vertical" margin={{ left: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis type="category" dataKey="name" width={150} tick={{ fontSize: 10 }} tickFormatter={(n) => (n.length > 24 ? `${n.slice(0, 24)}…` : n)} />
                    <Tooltip formatter={(v, n) => (n === 'Unités' ? v : money(v))} />
                    <Legend />
                    <Bar dataKey="units" name="Unités" fill="#f97316" radius={[0, 3, 3, 0]} />
                    <Bar dataKey="revenue" name="CA TTC" fill="#1e3a5f" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <div className="bg-white rounded-lg shadow-sm p-4" data-testid="chart-trend">
              <h3 className="font-semibold text-gray-900 mb-3">Évolution sur 12 mois</h3>
              <ResponsiveContainer width="100%" height={300}>
                <ComposedChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v, n) => (n === 'Commandes' ? v : money(v))} />
                  <Legend />
                  <Bar dataKey="revenue" name="CA TTC" fill="#fdba74" radius={[3, 3, 0, 0]} />
                  <Line type="monotone" dataKey="margin" name="Marge HT" stroke="#16a34a" strokeWidth={2} />
                  <Line type="monotone" dataKey="orders" name="Commandes" stroke="#1e3a5f" strokeDasharray="4 2" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow-sm overflow-x-auto" data-testid="report-top-table">
            <table className="w-full text-sm min-w-[720px]">
              <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
                <tr><th className="px-4 py-2 text-left">#</th><th className="px-4 py-2 text-left">Pièce</th><th className="px-4 py-2 text-right">Unités</th><th className="px-4 py-2 text-right">Commandes</th><th className="px-4 py-2 text-right">CA TTC</th><th className="px-4 py-2 text-right">Marge HT</th><th className="px-4 py-2 text-right">Taux</th></tr>
              </thead>
              <tbody className="divide-y">
                {data.top_products.map((t, i) => (
                  <tr key={t.product_id} data-testid={`report-top-row-${t.product_id}`}>
                    <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                    <td className="px-4 py-2"><div className="flex items-center gap-2"><img src={t.image} alt="" className="w-8 h-8 object-contain bg-gray-50 rounded" /><div><p className="font-medium text-gray-900">{t.name}</p><p className="text-xs text-gray-400">{t.brand}</p></div></div></td>
                    <td className="px-4 py-2 text-right font-semibold">{t.units}</td>
                    <td className="px-4 py-2 text-right">{t.orders}</td>
                    <td className="px-4 py-2 text-right">{money(t.revenue)}</td>
                    <td className={`px-4 py-2 text-right font-medium ${t.margin < 0 ? 'text-red-600' : 'text-green-700'}`}>{money(t.margin)}</td>
                    <td className="px-4 py-2 text-right text-gray-600">{t.revenue ? `${((t.margin / (t.revenue / (1 + data.vat_rate / 100))) * 100).toFixed(0)} %` : '—'}</td>
                  </tr>
                ))}
                {data.top_products.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-400">Aucune vente sur ce mois.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminReportsPanel;
